import React, { createContext, useContext, useState } from 'react';

const CartContext = createContext();

// Helper: Treat price of 1 as 0 for display (complimentary item indicator)
// But keep original price for API calls
const normalizePrice = (price) => {
  return price === 1 ? 0 : price;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  const addToCart = (item) => {
    setCart(prevCart => {
      // Create a unique key based on item id, variations, and special instructions
      const itemKey = `${item.id}_${item.variations?.sort().join('_') || 'plain'}_${item.specialInstructions || ''}`;
      const existingItemIndex = prevCart.findIndex(
        cartItem => `${cartItem.id}_${cartItem.variations?.sort().join('_') || 'plain'}_${cartItem.specialInstructions || ''}` === itemKey
      );
      
      if (existingItemIndex !== -1) {
        // Item with same variations and instructions exists, update quantity
        const newCart = [...prevCart];
        newCart[existingItemIndex] = {
          ...newCart[existingItemIndex],
          quantity: newCart[existingItemIndex].quantity + (item.quantity || 1)
        };
        return newCart;
      }
      
      // Store both original price (for API) and display price (for UI)
      const originalPrice = item.price;
      const originalTotalPrice = item.totalPrice || item.price;
      
      return [...prevCart, { 
        ...item, 
        cartId: itemKey,
        // Original prices for API calls
        originalPrice: originalPrice,
        originalTotalPrice: originalTotalPrice,
        // Normalized prices for UI display
        price: normalizePrice(originalPrice),
        totalPrice: normalizePrice(originalTotalPrice),
        quantity: item.quantity || 1,
        variations: item.variations || [],
        groupedVariations: item.groupedVariations || {},
        specialInstructions: item.specialInstructions || ''
      }];
    });
  };

  const removeFromCart = (cartId) => {
    setCart(prevCart => prevCart.filter(item => item.cartId !== cartId));
  };

  const updateQuantity = (cartId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.cartId === cartId ? { ...item, quantity } : item
      )
    );
  };

  const updateInstructions = (cartId, instructions) => {
    setCart(prevCart =>
      prevCart.map(item =>
        item.cartId === cartId ? { ...item, specialInstructions: instructions } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  // Get total for display (normalized prices)
  const getTotal = () => {
    return cart.reduce((total, item) => {
      const itemPrice = item.totalPrice || item.price;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  // Get total for API (original prices)
  const getApiTotal = () => {
    return cart.reduce((total, item) => {
      const itemPrice = item.originalTotalPrice || item.originalPrice || item.totalPrice || item.price;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const getItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateInstructions,
      clearCart,
      getTotal,
      getApiTotal,
      getItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
